import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, RefreshCw, X, Check, CheckCheck, AlertTriangle, Eye, CalendarClock, Send, Ban, RotateCcw, Trash2, MessageSquarePlus, PhoneCall, FileSignature } from "lucide-react";
import { ApplicantHoldButton, AppointmentBadge } from "../../components/ReviewActions";
import { AdminCandidateOnboarding } from "./AdminCandidateOnboarding";
import { ReplaceRequestButton, ReplacementNote, RejectRequestButton, RejectedNote } from "../../components/ReplaceRequestButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });
const DEFAULT_VENUE = "NexSecure Technologies, Kaloor, Ernakulam-17";

const fmtDate = (d) => new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const WaIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
);

const waChatLink = (a) => {
  const digits = (a.mobile_number || "").replace(/\D/g, "");
  if (!digits) return null;
  const num = digits.length === 10 ? `91${digits}` : digits;
  const text = encodeURIComponent(`Hello ${a.first_name}, this is HR from NexSecure Technologies regarding your job application ${a.reference_number}.`);
  return `https://wa.me/${num}?text=${text}`;
};

const WaBadge = ({ app, consent, mobile }) => {
  const c = consent ?? app?.whatsapp_consent;
  if (c !== "true")
    return <span data-testid="wa-consent-badge-none" title="No WhatsApp consent given" className="inline-flex items-center text-[10px] tracking-wide uppercase text-slate-500 border border-white/15 px-2 py-0.5 shrink-0">No WA</span>;
  const link = app ? waChatLink(app) : null;
  const cls = "inline-flex items-center gap-1.5 text-[10px] tracking-wide uppercase font-semibold text-emerald-400 border border-emerald-500/50 bg-emerald-500/10 px-2 py-0.5 shrink-0";
  if (!link) return <span data-testid="wa-consent-badge" className={cls}><WaIcon /> WA Consent</span>;
  return (
    <a href={link} target="_blank" rel="noopener noreferrer" data-testid="wa-consent-badge" title="Open WhatsApp chat with this candidate" className={`${cls} hover:bg-emerald-500/25 hover:border-emerald-400 transition-colors`}>
      <WaIcon /> WA Consent
    </a>
  );
};

const statusStyle = (s) =>
  s === "shortlisted" ? "text-emerald-400 border-emerald-500/50"
    : s === "interview" ? "text-sky-300 border-sky-500/50"
    : s === "rejected" ? "text-red-400 border-red-500/50"
    : s === "blacklisted" ? "text-red-300 border-red-500/60 bg-red-500/10"
    : s === "reviewed" ? "text-amber-300 border-amber-500/50"
    : s === "closed" ? "text-slate-300 border-slate-400/50 bg-white/5"
    : "text-slate-400 border-white/15";

const followupState = (f) => !f ? null : f.closed_at && !f.reopened_at ? { label: "Auto-closed", cls: "text-slate-300 border-slate-400/50", key: "closed" }
  : f.reopened_at ? { label: "Reopened", cls: "text-emerald-200 border-emerald-500/40", key: "reopened" }
  : f.called_back ? { label: `Called back · ${f.called_back.by}`, cls: "text-emerald-200 border-emerald-500/60 bg-emerald-500/10", key: "called" }
  : f.replied_at ? { label: "Candidate replied", cls: "text-emerald-300 border-emerald-500/50", key: "replied" }
  : f.reminder_sent_at ? { label: "Reminder sent", cls: "text-amber-200 border-amber-500/50", key: "pending" }
  : { label: `Follow-up ${fmtInterviewDate(f.followup_date)}`, cls: "text-violet-200 border-violet-400/50", key: "pending" };

const FOLLOWUP_FILTERS = [["all", "All"], ["pending", "Pending"], ["replied", "Replied"], ["called", "Called back"], ["closed", "Auto-closed"], ["reopened", "Reopened"]];

const fmtInterviewDate = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || "");
  return m ? `${m[3]}-${m[2]}-${m[1]}` : (v || "");
};
const fmtInterviewTime = (v) => {
  const m = /^(\d{2}):(\d{2})/.exec(v || "");
  if (!m) return v || "";
  let h = +m[1];
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m[2]} ${ap}`;
};

const blacklistExpiry = (iso) => {
  const start = new Date(iso);
  if (isNaN(start)) return null;
  const exp = new Date(start);
  exp.setFullYear(exp.getFullYear() + 1);
  return { date: exp.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }), expired: exp < new Date() };
};

const AckTick = ({ s }) => {
  if (!s) return null;
  if (s === "delivered" || s === "opened" || s === "clicked")
    return <span title={`Acknowledgment email ${s} — candidate received it`} data-testid="ack-tick-delivered" className="inline-flex items-center text-emerald-400"><CheckCheck size={14} /></span>;
  if (s === "bounced" || s === "complained" || s === "failed")
    return <span title={`Acknowledgment email ${s} — resend manually`} data-testid="ack-tick-failed" className="inline-flex items-center text-red-400"><AlertTriangle size={14} /></span>;
  return <span title="Acknowledgment email sent — awaiting delivery confirmation" data-testid="ack-tick-sent" className="inline-flex items-center text-slate-500"><Check size={14} /></span>;
};

export const AdminCareerApps = ({ onAuthFail, user, onShortlistToLetters }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const [webhook, setWebhook] = useState({ configured: false });
  const [webhookSecret, setWebhookSecret] = useState("");
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [refQuery, setRefQuery] = useState("");
  const [lookingUp, setLookingUp] = useState(false);

  const [interviewFor, setInterviewFor] = useState(null);
  const [interviewForm, setInterviewForm] = useState({ date: "", time: "", designation: "", venue: "", notes: "" });
  const [venues, setVenues] = useState([DEFAULT_VENUE]);
  const [newVenue, setNewVenue] = useState(null);
  const [savingVenue, setSavingVenue] = useState(false);
  const [sendingInterview, setSendingInterview] = useState(false);
  const canCallInterview = ["admin", "ceo", "hr"].includes(user?.role);
  const isCeo = user?.email === "ceo@nexsecure.in" || user?.role === "ceo";

  const [remarkFor, setRemarkFor] = useState(null);
  const [remarkForm, setRemarkForm] = useState({ remark: "", followup_date: "" });
  const [savingRemark, setSavingRemark] = useState(false);
  const openRemark = (a) => { setRemarkFor(a); setRemarkForm({ remark: "", followup_date: "" }); };
  const saveRemark = async (e) => {
    e.preventDefault();
    if (!remarkForm.remark.trim() || !remarkForm.followup_date) { toast.error("Enter the remark and pick a follow-up date."); return; }
    setSavingRemark(true);
    try {
      const { data } = await api.post(`/career-applications/${remarkFor.id}/remark`, { remark: remarkForm.remark.trim(), followup_date: remarkForm.followup_date });
      setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      if (detail?.id === data.id) setDetail(data);
      toast.success(`Remark saved — reminder email goes out on ${fmtInterviewDate(remarkForm.followup_date)}`);
      setRemarkFor(null);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      toast.error(err?.response?.data?.detail || "Could not save remark");
    } finally { setSavingRemark(false); }
  };

  const [blacklistOpen, setBlacklistOpen] = useState(false);
  const [blacklistReason, setBlacklistReason] = useState("");
  const [blacklisting, setBlacklisting] = useState(false);
  const [blacklist, setBlacklist] = useState([]);
  const [blacklistQuery, setBlacklistQuery] = useState("");

  const loadBlacklist = useCallback(async () => {
    if (!["admin", "ceo", "hr"].includes(user?.role)) return;
    try {
      const { data } = await api.get("/career-blacklist");
      setBlacklist(data);
    } catch { /* ignore */ }
  }, [user?.role]);

  const addVenue = async () => {
    const v = (newVenue || "").trim();
    if (v.length < 3) { toast.error("Enter the venue / location."); return; }
    setSavingVenue(true);
    try {
      const { data } = await api.post("/interview-venues", { venue: v });
      setVenues(data.venues);
      setInterviewForm((p) => ({ ...p, venue: data.added }));
      setNewVenue(null);
      toast.success("Venue added");
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error(err?.response?.data?.detail || "Could not add venue");
    } finally {
      setSavingVenue(false);
    }
  };

  const openInterview = (app) => {
    setInterviewForm({ date: "", time: "", designation: app.position || "", venue: DEFAULT_VENUE, notes: "" });
    setNewVenue(null);
    api.get("/interview-venues").then(({ data }) => setVenues(data.venues || [DEFAULT_VENUE])).catch(() => {});
    setInterviewFor(app);
  };

  const submitInterview = async (e) => {
    e.preventDefault();
    if (!interviewFor) return;
    const f = interviewForm;
    if (!f.date || !f.time || !f.designation.trim() || !f.venue.trim()) {
      toast.error("Please fill date, time, designation and venue.");
      return;
    }
    setSendingInterview(true);
    try {
      const { data } = await api.post(`/career-applications/${interviewFor.id}/call-for-interview`, f);
      updateCareerApp(data);
      toast.success(`Interview email sent to ${interviewFor.first_name}`);
      setInterviewFor(null);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error(err?.response?.data?.detail || "Could not send the interview email");
    } finally {
      setSendingInterview(false);
    }
  };

  const lookupRef = async (e) => {
    e.preventDefault();
    if (!refQuery.trim()) return;
    setLookingUp(true);
    try {
      const { data } = await api.get("/career-onboarding/admin/lookup", { params: { ref: refQuery.trim() } });
      setDetail(data.application);
      toast.success(`Found: ${data.application.first_name} ${data.application.surname} (${data.application.reference_number})`);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error(err?.response?.data?.detail || "No application found for that reference");
    } finally {
      setLookingUp(false);
    }
  };

  const loadWebhook = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/email-webhook");
      setWebhook(data);
    } catch { /* ignore */ }
  }, []);

  const saveWebhook = async (e) => {
    e.preventDefault();
    setSavingWebhook(true);
    try {
      await api.post("/admin/email-webhook", { secret: webhookSecret });
      setWebhook({ configured: true });
      setWebhookSecret("");
      toast.success("Delivery receipts activated — ticks will now update live");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the webhook secret");
    } finally {
      setSavingWebhook(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/career-applications");
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error("Could not load career applications");
    } finally {
      setLoading(false);
    }
  }, [onAuthFail]);

  useEffect(() => { load(); loadWebhook(); loadBlacklist(); }, [load, loadWebhook, loadBlacklist]);

  const markStatus = async (id, status) => {
    try {
      await api.patch(`/career-applications/${id}`, { status });
      setItems((p) => p.map((x) => (x.id === id ? { ...x, status } : x)));
      if (detail?.id === id) setDetail((p) => ({ ...p, status }));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Status update failed");
    }
  };

  const [fuFilter, setFuFilter] = useState("all");
  const fuKey = (a) => followupState(a.followup)?.key;
  const fuCount = (k) => items.filter((a) => (k === "all" ? !!a.followup : fuKey(a) === k)).length;
  const shownItems = fuFilter === "all" ? items : items.filter((a) => fuKey(a) === fuFilter);

  const calledBack = async (id) => {
    const note = window.prompt("Called back — add a short note (optional):", "") ;
    if (note === null) return;
    try {
      const { data } = await api.post(`/career-applications/${id}/called-back`, { note });
      setItems((p) => p.map((x) => (x.id === id ? data : x)));
      if (detail?.id === id) setDetail(data);
      toast.success("Logged: candidate called back");
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      toast.error(err?.response?.data?.detail || "Could not log the call");
    }
  };

  const reopenApp = async (id) => {
    try {
      const { data } = await api.post(`/career-applications/${id}/reopen`);
      setItems((p) => p.map((x) => (x.id === id ? data : x)));
      if (detail?.id === id) setDetail(data);
      toast.success("Application reopened — status set to Reviewed");
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      toast.error(err?.response?.data?.detail || "Could not reopen application");
    }
  };

  const updateCareerApp = (updated) => {
    setItems((p) => p.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
    setDetail((p) => (p?.id === updated.id ? { ...p, ...updated } : p));
  };

  const openBlacklist = () => { setBlacklistReason(""); setBlacklistOpen(true); };

  const confirmBlacklist = async () => {
    if (!detail) return;
    setBlacklisting(true);
    try {
      const { data } = await api.post(`/career-applications/${detail.id}/blacklist`, { reason: blacklistReason.trim() });
      updateCareerApp(data);
      toast.success(`${detail.first_name} ${detail.surname} has been blacklisted`);
      setBlacklistOpen(false);
      loadBlacklist();
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error(err?.response?.data?.detail || "Could not blacklist candidate");
    } finally {
      setBlacklisting(false);
    }
  };

  const deleteApp = async (a) => {
    const name = `${a.first_name || ""} ${a.surname || ""}`.trim();
    if (!window.confirm(`Permanently delete the application of ${name || "this candidate"} (${a.reference_number})? This removes the candidate record, onboarding data and invites. This cannot be undone.`)) return;
    try {
      await api.delete(`/career-applications/${a.id}`);
      toast.success(`Application ${a.reference_number} deleted`);
      setItems((p) => p.filter((x) => x.id !== a.id));
      setDetail((p) => (p?.id === a.id ? null : p));
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error(err?.response?.data?.detail || "Could not delete application");
    }
  };

  const unblacklist = async (appId, name) => {
    if (!window.confirm(`Remove ${name || "this candidate"} from the blacklist? They will be able to apply to NexSecure Technologies again.`)) return;
    try {
      const { data } = await api.post(`/career-applications/${appId}/unblacklist`);
      toast.success("Removed from blacklist");
      loadBlacklist();
      setItems((p) => p.map((x) => (x.id === appId ? { ...x, status: data.restored_status, blacklist_warning: undefined } : x)));
      setDetail((p) => (p?.id === appId ? { ...p, status: data.restored_status, blacklist_warning: undefined } : p));
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error(err?.response?.data?.detail || "Could not remove from blacklist");
    }
  };


  const exportCsv = async () => {
    try {
      const res = await api.get("/career-applications/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `career-applications-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Spreadsheet downloaded");
    } catch {
      toast.error("Could not export applications");
    }
  };

  const requestFileReplacement = async (appId, kind, reason) => {
    try {
      const { data } = await api.post(`/career-applications/${appId}/request-replacement`, { kind, reason });
      toast.success("Replacement request emailed to the candidate");
      setDetail((p) => (p?.id === appId ? { ...p, replace_requests: { ...(p.replace_requests || {}), [kind]: { requested_at: data.requested_at, status: "pending" } } } : p));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the replacement request");
      throw err;
    }
  };

  const rejectFile = async (appId, kind, reason) => {
    try {
      const { data } = await api.post(`/career-applications/${appId}/reject-document`, { kind, reason });
      toast.success("Document rejected — candidate notified by email");
      setDetail((p) => (p?.id === appId ? { ...p, replace_requests: { ...(p.replace_requests || {}), [kind]: { requested_at: data.rejected_at, status: "pending", action: "rejected", reason } } } : p));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reject the document");
      throw err;
    }
  };

  const download = async (appId, kind, filename) => {
    try {
      const res = await api.get(`/career-applications/${appId}/file/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || kind;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download file");
    }
  };

  const DetailRow = ({ label, children }) => (
    <div className="grid grid-cols-[160px_1fr] gap-3 py-2 border-b border-white/5">
      <span className="text-[10px] tracking-[0.18em] uppercase text-slate-500 pt-0.5">{label}</span>
      <span className="text-sm text-slate-200">{children || "—"}</span>
    </div>
  );

  return (
    <div data-testid="admin-career-apps">
      <div className="border border-white/10 bg-white/[0.02] p-4 sm:p-5 mb-6" data-testid="email-webhook-settings">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500">Email Delivery Receipts (ticks beside candidate emails)</p>
          <span className={`text-[10px] tracking-[0.14em] uppercase border px-2.5 py-1 ${webhook.configured ? "text-emerald-300 border-emerald-500/50" : "text-amber-300 border-amber-500/40"}`} data-testid="email-webhook-chip">
            {webhook.configured ? "Receipts live" : "Setup needed"}
          </span>
        </div>
        {!webhook.configured && (
          <form onSubmit={saveWebhook} className="mt-3 flex flex-col sm:flex-row gap-3">
            <input required type="password" placeholder="Resend webhook signing secret (whsec_…)" className="flex-1 bg-white/[0.04] border border-white/15 focus:border-nx-gold outline-none px-4 py-2.5 text-sm text-white placeholder:text-slate-600 transition-colors" value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} data-testid="email-webhook-secret" autoComplete="off" />
            <button type="submit" disabled={savingWebhook} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-2.5 transition-colors disabled:opacity-60 shrink-0" data-testid="email-webhook-save">
              {savingWebhook ? "Saving…" : "Activate"}
            </button>
          </form>
        )}
        {!webhook.configured && (
          <p className="text-[11px] text-slate-600 mt-2">Resend dashboard → Webhooks → Add endpoint <span className="text-slate-400">https://nexsecure.in/api/resend/webhook</span> → tick email.delivered, email.bounced, email.complained → paste the signing secret here.</p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-marcellus text-xl text-white">Career Applications</h2>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={exportCsv} className="flex items-center gap-2 text-xs text-nx-navy bg-nx-gold hover:bg-nx-goldlight px-3 py-2 transition-colors" data-testid="career-apps-export">
            <Download size={13} /> Export CSV
          </button>
          <button onClick={load} className="flex items-center gap-2 text-xs text-slate-400 hover:text-white border border-white/15 px-3 py-2 transition-colors" data-testid="career-apps-refresh">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {["admin", "hr"].includes(user?.role) && (
        <form onSubmit={lookupRef} className="border border-white/10 bg-white/[0.02] p-4 sm:p-5 mb-6" data-testid="candidate-lookup">
          <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-3">Find Candidate by Reference Number</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input value={refQuery} onChange={(e) => setRefQuery(e.target.value)} placeholder="e.g. NS-APP-2026-… or candidate email" className="flex-1 bg-white/[0.04] border border-white/15 focus:border-nx-gold outline-none px-4 py-2.5 text-sm text-white placeholder:text-slate-600 transition-colors font-mono" data-testid="candidate-lookup-input" autoComplete="off" />
            <button type="submit" disabled={lookingUp} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-2.5 transition-colors disabled:opacity-60 shrink-0" data-testid="candidate-lookup-btn">
              {lookingUp ? "Searching…" : "Find Candidate"}
            </button>
          </div>
          <p className="text-[11px] text-slate-600 mt-2">The reference number is on the candidate's acknowledgment email. Onboarding details, documents and e-signatures open with the application.</p>
        </form>
      )}
      {canCallInterview && blacklist.length > 0 && (() => {
        const q = blacklistQuery.trim().toLowerCase();
        const filtered = q
          ? blacklist.filter((b) => [b.full_name, b.reference_number, b.mobile_number, b.position, b.reason, b.blacklisted_by]
              .some((v) => (v || "").toLowerCase().includes(q)))
          : blacklist;
        return (
        <div className="border border-red-500/30 bg-red-500/[0.04] p-4 sm:p-5 mb-6" data-testid="blacklist-register">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <p className="text-[10px] tracking-[0.24em] uppercase text-red-300/90 flex items-center gap-2"><Ban size={13} /> Blacklisted Candidates ({blacklist.length})</p>
            {blacklist.length > 5 && (
              <input
                value={blacklistQuery}
                onChange={(e) => setBlacklistQuery(e.target.value)}
                placeholder="Filter by name, reference, mobile, reason…"
                className="ml-auto w-full sm:w-72 bg-white/5 border border-white/15 text-white text-xs px-3 py-1.5 focus:border-red-500/60 outline-none"
                data-testid="blacklist-search"
              />
            )}
          </div>
          <div className="flex flex-col divide-y divide-white/5">
            {filtered.length === 0 ? (
              <p className="text-xs text-slate-500 py-3" data-testid="blacklist-no-results">No blacklisted candidate matches “{blacklistQuery}”.</p>
            ) : filtered.map((b) => (
              <div key={b.application_id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 py-2.5" data-testid={`blacklist-row-${b.application_id}`}>
                <span className="text-xs font-mono text-nx-goldlight w-32 shrink-0">{b.reference_number}</span>
                <div className="min-w-40">
                  <p className="text-sm text-white">{b.full_name}</p>
                  <p className="text-[11px] text-slate-500">{b.mobile_number}{b.position ? ` · ${b.position}` : ""}</p>
                </div>
                <span className="text-[11px] text-red-300/80" data-testid={`blacklist-audit-${b.application_id}`}>
                  Blacklisted {fmtDate(b.blacklist_date)}{b.blacklisted_by ? ` · by ${b.blacklisted_by}` : ""}
                </span>
                {(() => {
                  const e = blacklistExpiry(b.blacklist_date);
                  if (!e) return null;
                  return (
                    <span className={`text-[10px] tracking-[0.1em] uppercase border px-2 py-0.5 ${e.expired ? "text-slate-500 border-white/15" : "text-amber-300 border-amber-500/40"}`} data-testid={`blacklist-expiry-${b.application_id}`}>
                      {e.expired ? `Expired · ${e.date}` : `Lifts on ${e.date}`}
                    </span>
                  );
                })()}
                {b.reason ? <span className="text-[11px] text-slate-400 italic min-w-32 flex-1">“{b.reason}”</span> : <span className="flex-1" />}
                {isCeo ? (
                  <button onClick={() => unblacklist(b.application_id, b.full_name)} className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase text-amber-300 border border-amber-500/50 hover:bg-amber-500/15 px-3 py-1.5 transition-colors ml-auto" data-testid={`blacklist-unblacklist-${b.application_id}`}>
                    <RotateCcw size={11} /> Un-blacklist
                  </button>
                ) : (
                  <span className="text-[10px] tracking-[0.1em] uppercase text-slate-500 ml-auto" data-testid={`blacklist-ceo-only-${b.application_id}`}>CEO can lift</span>
                )}
              </div>
            ))}
          </div>
        </div>
        );
      })()}
      {fuCount("all") > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="career-followup-filters">
          <span className="text-[10px] tracking-[0.18em] uppercase text-slate-600 mr-1">Follow-ups</span>
          {FOLLOWUP_FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => setFuFilter(k)} data-testid={`career-followup-filter-${k}`}
              className={`text-[11px] tracking-[0.14em] uppercase px-3 py-1.5 border transition-colors ${fuFilter === k ? "border-violet-400/60 text-violet-200 bg-violet-500/[0.08]" : "border-white/10 text-slate-500 hover:text-white"}`}>
              {k === "all" ? `All applications` : `${label} (${fuCount(k)})`}
            </button>
          ))}
        </div>
      )}
      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="career-apps-empty">No career applications yet.</p>
      ) : shownItems.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="career-apps-filter-empty">No candidates in this follow-up state.</p>
      ) : (
        <div className="border border-white/10">
          {shownItems.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-4 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors" data-testid={`career-app-row-${a.id}`}>
              <img
                src={`${API}/career-applications/${a.id}/file/photo`}
                alt=""
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="h-10 w-10 rounded object-cover border border-white/15 shrink-0 bg-white/5"
                data-testid={`career-app-thumb-${a.id}`}
              />
              <span className="text-xs font-mono text-nx-goldlight w-32" data-testid={`career-app-ref-${a.id}`}>{a.reference_number}</span>
              <div className="min-w-44">
                <p className="text-white text-sm font-medium inline-flex items-center gap-2">{a.first_name} {a.surname} <WaBadge app={a} /></p>
                <p className="text-[11px] text-slate-500">{fmtDate(a.created_at)}</p>
              </div>
              <span className="text-[10px] tracking-[0.14em] uppercase text-nx-goldlight border border-nx-gold/40 px-2.5 py-1">{a.position}</span>
              <span className="text-xs text-slate-400 hidden md:inline-flex items-center gap-1.5">{a.email} <AckTick s={a.ack_email_status} /></span>
              <span className={`text-[10px] tracking-[0.16em] uppercase border px-2.5 py-1 ${statusStyle(a.status || "new")}`} data-testid={`career-app-status-${a.id}`}>{a.status || "new"}</span>
              {a.status === "interview" && a.interview?.date && (
                <span className="text-[10px] tracking-[0.12em] uppercase text-sky-200 border border-sky-500/40 bg-sky-500/5 px-2.5 py-1" data-testid={`career-app-interview-when-${a.id}`}>
                  {fmtInterviewDate(a.interview.date)}{a.interview.time ? ` · ${fmtInterviewTime(a.interview.time)}` : ""}
                </span>
              )}
              {a.onboarding_status && <span className="text-[10px] tracking-[0.14em] uppercase text-sky-300 border border-sky-500/40 px-2.5 py-1" data-testid={`career-app-onboarding-status-${a.id}`}>Onboarding: {a.onboarding_status}</span>}
              <AppointmentBadge appointment={a.appointment} testId={`career-app-appointment-${a.id}`} />
              {followupState(a.followup) && <span className={`text-[10px] tracking-[0.12em] uppercase border px-2.5 py-1 ${followupState(a.followup).cls}`} title={a.followup.remark} data-testid={`career-app-followup-${a.id}`}>{followupState(a.followup).label}</span>}
              {canCallInterview && a.followup?.replied_at && !a.followup?.called_back && !a.followup?.closed_at && (
                <button onClick={() => calledBack(a.id)} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/10 px-3 py-2 transition-colors" data-testid={`career-app-called-back-${a.id}`}><PhoneCall size={13} /> Called back</button>
              )}
              {isCeo && a.status === "closed" && (
                <button onClick={() => reopenApp(a.id)} className="ml-auto inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10 px-3 py-2 transition-colors" data-testid={`career-app-reopen-${a.id}`}><RotateCcw size={13} /> Reopen</button>
              )}
              {isCeo && !["rejected", "blacklisted", "closed"].includes(a.status || "new") && (
                <button onClick={() => openRemark(a)} className="ml-auto inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-violet-200 border border-violet-400/40 hover:bg-violet-500/10 px-3 py-2 transition-colors" data-testid={`career-app-add-remark-${a.id}`}><MessageSquarePlus size={13} /> Add Remark</button>
              )}
              {isCeo && !["rejected", "blacklisted", "closed"].includes(a.status || "new") && (
                <ApplicantHoldButton api={api} application={a} onDone={load} />
              )}
              {onShortlistToLetters && !["rejected", "blacklisted", "closed"].includes(a.status || "new") && !a.appointment && (
                <button onClick={() => onShortlistToLetters(a)} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-nx-goldlight border border-nx-gold/50 hover:bg-nx-gold/10 px-3 py-2 transition-colors" data-testid={`career-app-shortlist-docs-${a.id}`} title="Shortlist and open the appointment form pre-filled from this application">
                  <FileSignature size={13} /> Shortlist &amp; Send Documents
                </button>
              )}
              <button onClick={() => setDetail(a)} className={`${isCeo && !["rejected", "blacklisted"].includes(a.status || "new") ? "" : "ml-auto "}text-xs text-white border border-white/20 hover:border-nx-gold/60 px-4 py-2 transition-colors`} data-testid={`career-app-view-${a.id}`}>View</button>
              {isCeo && (a.status || "new") !== "blacklisted" && (
                <button onClick={() => deleteApp(a)} title="Delete application (CEO only)" className="text-red-300 border border-red-500/40 hover:bg-red-500/15 p-2 transition-colors" data-testid={`career-app-delete-${a.id}`}><Trash2 size={14} /></button>
              )}
            </div>
          ))}
        </div>
      )}

      {detail && (
        <div data-lenis-prevent className="fixed inset-0 z-[95] bg-black/70 overflow-y-auto" data-testid="career-app-detail">
          <div className="max-w-6xl mx-auto my-3 sm:my-10 bg-nx-navy border border-white/15 p-4 sm:p-7">
            <div className="flex items-start justify-between gap-3 mb-5">
              <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 min-w-0">
                <img
                  src={`${API}/career-applications/${detail.id}/file/photo`}
                  alt="Passport photo"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  className="h-24 w-20 rounded object-cover border border-white/15 shrink-0 bg-white/5"
                  data-testid="career-app-detail-photo"
                />
                <div className="min-w-0">
                  <span className="text-xs font-mono text-nx-goldlight">{detail.reference_number}</span>
                  <h3 className="font-marcellus text-xl sm:text-2xl text-white mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">{detail.first_name} {detail.middle_name} {detail.surname} <WaBadge app={detail} /></h3>
                  <p className="text-xs text-slate-500 mt-1">{detail.position} · {detail.department} · {fmtDate(detail.created_at)}</p>
                </div>
              </div>
              <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-white p-1" data-testid="career-app-detail-close"><X size={20} /></button>
            </div>

            {detail.blacklist_warning && (
              <div className="mb-5 border border-red-500/60 bg-red-500/10 px-4 py-3 flex items-start gap-2.5" data-testid="career-app-blacklist-warning">
                <AlertTriangle size={16} className="text-red-400 mt-0.5 shrink-0" />
                <p className="text-sm text-red-200 leading-relaxed">
                  <b>Warning:</b> This candidate previously applied on {detail.blacklist_warning.application_date || "—"}, was called for interview on {detail.blacklist_warning.interview_date || "—"}, and did not attend. They were blacklisted on {detail.blacklist_warning.blacklist_date || "—"}.
                  {detail.blacklist_warning.reason ? <span className="block mt-1 text-red-300/90"><b>Reason:</b> {detail.blacklist_warning.reason}</span> : null}
                </p>
              </div>
            )}

            <DetailRow label="Email"><span className="inline-flex items-center gap-1.5">{detail.email} <AckTick s={detail.ack_email_status} /></span></DetailRow>
            <DetailRow label="Mobile">{detail.mobile_number}</DetailRow>
            <DetailRow label="Emergency Contact">{detail.emergency_contact}</DetailRow>
            <DetailRow label="Father / Mother">{detail.father_name} / {detail.mother_name}</DetailRow>
            <DetailRow label="Present Address">{[detail.present_address, detail.present_state || detail.present_state_free, detail.present_pincode, detail.present_country === "Other" ? detail.present_country_free : detail.present_country].filter(Boolean).join(", ")}</DetailRow>
            <DetailRow label="Permanent Address">{detail.same_address === "true" ? "Same as present" : [detail.permanent_address, detail.permanent_state || detail.permanent_state_free, detail.permanent_pincode, detail.permanent_country === "Other" ? detail.permanent_country_free : detail.permanent_country].filter(Boolean).join(", ")}</DetailRow>
            <DetailRow label="Date of Birth">{detail.dob_day}/{detail.dob_month}/{detail.dob_year}</DetailRow>
            <DetailRow label="Gender / Marital">{detail.gender} · {detail.marital_status}{detail.spouse_name ? ` (Spouse: ${detail.spouse_name})` : ""}</DetailRow>
            <DetailRow label="Current Location">{detail.current_location}</DetailRow>
            <DetailRow label="Qualifications">{(detail.qualifications || []).map((q, i) => <div key={i}>{q.degree} ({q.year}) — {q.college} — {q.percentage}%{q.courses ? ` · ${q.courses}` : ""}</div>)}</DetailRow>
            <DetailRow label="Preferred Location">{detail.preferred_location_1}{detail.preferred_location_2 ? ` / ${detail.preferred_location_2}` : ""}</DetailRow>
            <DetailRow label="Experience">{detail.experience_status}</DetailRow>
            {(detail.experiences || []).length > 0 && (
              <DetailRow label="Work History">{(detail.experiences || []).map((ex, i) => <div key={i}>{ex.designation} at {ex.employer} ({ex.from_month}/{ex.from_year} — {ex.current ? "Present" : `${ex.to_month}/${ex.to_year}`})</div>)}</DetailRow>
            )}
            {detail.experience_status === "Experienced" && <DetailRow label="CTC">Current ₹{detail.current_ctc} · Notice: {detail.notice_period}</DetailRow>}
            <DetailRow label="Expected CTC">₹{detail.expected_ctc}</DetailRow>
            <DetailRow label="Source">{detail.source}</DetailRow>
            <DetailRow label="Resume">
              <span className="inline-flex flex-wrap items-center gap-3">
                <span className="text-slate-200">{detail.resume_filename || "resume.pdf"}</span>
                <a href={`${API}/career-applications/${detail.id}/file/resume`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-nx-goldlight border border-nx-gold/40 hover:bg-nx-gold/10 px-2.5 py-1 transition-colors" data-testid="career-app-view-resume"><Eye size={12} /> View PDF</a>
              </span>
            </DetailRow>

            <div className="flex flex-wrap gap-2 mt-6">
              {[["resume", "Resume", detail.resume_filename], ["photo", "Passport Photo", detail.photo_filename], ["cover", "Cover Letter", detail.cover_filename]].filter(([k]) => k !== "cover" || detail.cover_path).map(([kind, label, fname]) => (
                <span key={kind} className="inline-flex items-center gap-1.5" data-testid={`career-app-file-${kind}`}>
                  <button onClick={() => download(detail.id, kind, fname)} className="flex items-center gap-1.5 text-xs text-white border border-white/20 hover:border-nx-gold/60 px-3 py-2 transition-colors" data-testid={`career-app-dl-${kind}`}><Download size={12} /> {label}</button>
                  {canCallInterview && <ReplaceRequestButton label={label} testId={`career-app-replace-${kind}`} onConfirm={(reason) => requestFileReplacement(detail.id, kind, reason)} className="flex items-center gap-1 text-[10px] uppercase px-2.5 py-2 border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 transition-colors" />}
                  {canCallInterview && detail.replace_requests?.[kind]?.action !== "rejected" && <RejectRequestButton label={label} testId={`career-app-reject-${kind}`} onConfirm={(reason) => rejectFile(detail.id, kind, reason)} className="flex items-center gap-1 text-[10px] uppercase px-2.5 py-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors" />}
                  {detail.replace_requests?.[kind]?.status === "pending" && detail.replace_requests[kind].action === "rejected" && (
                    <span className="inline-flex items-center gap-1.5"><span className="text-[10px] uppercase border border-red-500/50 text-red-300 bg-red-500/10 px-1.5 py-0.5">Rejected</span><RejectedNote at={detail.replace_requests[kind].requested_at} reason={detail.replace_requests[kind].reason} testId={`career-app-reject-note-${kind}`} /></span>
                  )}
                  {detail.replace_requests?.[kind]?.status === "pending" && detail.replace_requests[kind].action !== "rejected" && <ReplacementNote at={detail.replace_requests[kind].requested_at} testId={`career-app-replace-note-${kind}`} />}
                  {detail.replace_requests?.[kind]?.status === "replaced" && <span className="text-[10px] text-sky-300">Re-uploaded by candidate</span>}
                </span>
              ))}
              <span className="flex flex-wrap gap-2 ml-auto justify-end">
                {canCallInterview && detail.interview?.reschedule_request && (
                  <span className="inline-flex items-start gap-2 max-w-xs text-[11px] leading-snug text-amber-200 bg-amber-500/10 border border-amber-500/50 px-3 py-2" data-testid="career-app-reschedule-notice" title={`Email received ${new Date(detail.interview.reschedule_request.at).toLocaleString("en-IN")}${detail.interview.reschedule_request.subject ? ` — "${detail.interview.reschedule_request.subject}"` : ""}`}>
                    <AlertTriangle size={13} className="shrink-0 mt-0.5 text-amber-300" /> This candidate has requested a change of interview date and time. Please check hr@nexsecure.in for their email.
                  </span>
                )}
                {canCallInterview && (detail.status || "new") !== "rejected" && (
                  <button onClick={() => openInterview(detail)} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-sky-300 border border-sky-500/40 hover:bg-sky-500/10 px-3 py-2" data-testid="career-app-call-interview"><CalendarClock size={13} /> Call for Interview</button>
                )}
                {isCeo && detail.status === "closed" && (
                  <button onClick={() => reopenApp(detail.id)} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10 px-3 py-2" data-testid="career-app-reopen"><RotateCcw size={13} /> Reopen Application</button>
                )}
                {(detail.status || "new") === "new" && <button onClick={() => markStatus(detail.id, "reviewed")} className="text-[11px] tracking-[0.1em] uppercase text-white border border-white/20 hover:border-nx-gold/60 px-3 py-2" data-testid="career-app-mark-reviewed">Mark Reviewed</button>}
                {((detail.status || "new") === "new" || detail.status === "reviewed") && (
                  <>
                    <button onClick={() => markStatus(detail.id, "shortlisted")} className="text-[11px] tracking-[0.1em] uppercase text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10 px-3 py-2" data-testid="career-app-shortlist">Shortlist</button>
                    <button onClick={() => markStatus(detail.id, "rejected")} className="text-[11px] tracking-[0.1em] uppercase text-red-300 border border-red-500/40 hover:bg-red-500/10 px-3 py-2" data-testid="career-app-reject">Reject</button>
                  </>
                )}
                {canCallInterview && (detail.status || "new") !== "blacklisted" && (
                  <button onClick={openBlacklist} className="text-[11px] tracking-[0.1em] uppercase text-red-300 border border-red-500/50 hover:bg-red-500/15 px-3 py-2" data-testid="career-app-blacklist">Blacklist Candidate</button>
                )}
                {isCeo && (detail.status || "new") === "blacklisted" && (
                  <button onClick={() => unblacklist(detail.id, `${detail.first_name} ${detail.surname}`)} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-amber-300 border border-amber-500/50 hover:bg-amber-500/15 px-3 py-2" data-testid="career-app-unblacklist"><RotateCcw size={12} /> Un-blacklist</button>
                )}
              </span>
            </div>
            <AdminCandidateOnboarding application={detail} user={user} onChanged={updateCareerApp} onAuthFail={onAuthFail} />
          </div>
        </div>
      )}

      {blacklistOpen && detail && (
        <div data-lenis-prevent className="fixed inset-0 z-[110] bg-black/75 flex items-start justify-center overflow-y-auto p-4" data-testid="blacklist-modal">
          <div className="bg-nx-navy border border-red-500/40 w-full max-w-lg mt-4 sm:mt-16 mb-10">
            <div className="flex items-center justify-between bg-[#0a1526] px-6 py-4 border-b border-white/10">
              <h3 className="font-marcellus text-xl text-white inline-flex items-center gap-2"><Ban size={18} className="text-red-400" /> Blacklist Candidate</h3>
              <button type="button" onClick={() => setBlacklistOpen(false)} className="text-slate-400 hover:text-white" data-testid="blacklist-close"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-red-200 leading-relaxed" data-testid="blacklist-confirm-text">
                Are you sure you want to blacklist <b className="text-white">{detail.first_name} {detail.surname}</b>? They will not be able to apply to NexSecure Technologies for 1 year.
              </p>
              <label className="block">
                <span className="text-[10px] tracking-[0.16em] uppercase text-slate-500">Reason <span className="text-slate-600">(optional)</span></span>
                <textarea rows={3} value={blacklistReason} onChange={(e) => setBlacklistReason(e.target.value)} placeholder="e.g. Did not attend scheduled interview without notice" className="mt-1 w-full bg-white/5 border border-white/15 text-white text-sm px-3 py-2 focus:border-red-500/60 outline-none resize-none" data-testid="blacklist-reason" />
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button type="button" onClick={() => setBlacklistOpen(false)} className="text-xs tracking-[0.12em] uppercase text-slate-400 hover:text-white px-4 py-2.5" data-testid="blacklist-cancel">Cancel</button>
              <button type="button" onClick={confirmBlacklist} disabled={blacklisting} className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white text-xs font-semibold tracking-[0.14em] uppercase px-5 py-2.5 transition-colors disabled:opacity-60" data-testid="blacklist-confirm">
                <Ban size={14} /> {blacklisting ? "Blacklisting…" : "Confirm Blacklist"}
              </button>
            </div>
          </div>
        </div>
      )}

      {interviewFor && (
        <div data-lenis-prevent className="fixed inset-0 z-[110] bg-black/75 flex items-start justify-center overflow-y-auto p-4" data-testid="interview-modal">
          <form onSubmit={submitInterview} className="bg-nx-navy border border-nx-gold/30 w-full max-w-lg mt-4 sm:mt-10 mb-10">
            <div className="flex items-center justify-between bg-[#0a1526] px-6 py-4 border-b border-white/10">
              <h3 className="font-marcellus text-xl text-white inline-flex items-center gap-2"><CalendarClock size={18} className="text-sky-300" /> Call for Interview</h3>
              <button type="button" onClick={() => setInterviewFor(null)} className="text-slate-400 hover:text-white" data-testid="interview-close"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-xs text-slate-400">Sending to <b className="text-white">{interviewFor.first_name} {interviewFor.surname}</b> · {interviewFor.email}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[10px] tracking-[0.16em] uppercase text-slate-500">Interview Date</span>
                  <input type="date" required value={interviewForm.date} onChange={(e) => setInterviewForm((p) => ({ ...p, date: e.target.value }))} className="mt-1 w-full bg-white/5 border border-white/15 text-white text-sm px-3 py-2 focus:border-nx-gold/60 outline-none" data-testid="interview-date" />
                </label>
                <label className="block">
                  <span className="text-[10px] tracking-[0.16em] uppercase text-slate-500">Interview Time</span>
                  <input type="time" required value={interviewForm.time} onChange={(e) => setInterviewForm((p) => ({ ...p, time: e.target.value }))} className="mt-1 w-full bg-white/5 border border-white/15 text-white text-sm px-3 py-2 focus:border-nx-gold/60 outline-none" data-testid="interview-time" />
                </label>
              </div>
              <label className="block">
                <span className="text-[10px] tracking-[0.16em] uppercase text-slate-500">Designation</span>
                <input type="text" required value={interviewForm.designation} onChange={(e) => setInterviewForm((p) => ({ ...p, designation: e.target.value }))} className="mt-1 w-full bg-white/5 border border-white/15 text-white text-sm px-3 py-2 focus:border-nx-gold/60 outline-none" data-testid="interview-designation" />
              </label>
              <div className="block">
                <span className="text-[10px] tracking-[0.16em] uppercase text-slate-500">Interview Venue / Location</span>
                <div className="mt-1 flex gap-2">
                  <select required value={interviewForm.venue} onChange={(e) => setInterviewForm((p) => ({ ...p, venue: e.target.value }))} className="w-full bg-white/5 border border-white/15 text-white text-sm px-3 py-2 focus:border-nx-gold/60 outline-none" data-testid="interview-venue">
                    {venues.map((v) => <option key={v} value={v} className="bg-nx-navy text-white">{v}</option>)}
                  </select>
                  <button type="button" onClick={() => setNewVenue(newVenue === null ? "" : null)} title="Add another venue" className="shrink-0 w-9 border border-nx-gold/50 text-nx-goldlight hover:bg-nx-gold/15 text-lg leading-none transition-colors" data-testid="interview-venue-add">{newVenue === null ? "+" : "×"}</button>
                </div>
                {newVenue !== null && (
                  <div className="mt-2 flex gap-2" data-testid="interview-venue-new">
                    <input type="text" autoFocus value={newVenue} onChange={(e) => setNewVenue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addVenue(); } }} placeholder="New venue / location (e.g. NexSecure Branch, Thrissur)" className="w-full bg-white/5 border border-white/15 text-white text-sm px-3 py-2 focus:border-nx-gold/60 outline-none" data-testid="interview-venue-new-input" />
                    <button type="button" onClick={addVenue} disabled={savingVenue} className="shrink-0 text-[11px] tracking-[0.1em] uppercase bg-nx-gold text-nx-navy font-semibold px-3 py-2 disabled:opacity-50" data-testid="interview-venue-save">{savingVenue ? "Saving…" : "Add"}</button>
                  </div>
                )}
              </div>
              <label className="block">
                <span className="text-[10px] tracking-[0.16em] uppercase text-slate-500">Additional Notes <span className="text-slate-600">(optional)</span></span>
                <textarea rows={3} value={interviewForm.notes} onChange={(e) => setInterviewForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Contact person, directions, etc." className="mt-1 w-full bg-white/5 border border-white/15 text-white text-sm px-3 py-2 focus:border-nx-gold/60 outline-none resize-none" data-testid="interview-notes" />
              </label>
              <div className="border border-white/10 bg-white/[0.03] px-4 py-3" data-testid="interview-doc-checklist">
                <p className="text-[10px] tracking-[0.16em] uppercase text-slate-500">Documents the candidate will be asked to carry <span className="text-slate-600 normal-case tracking-normal">· included in the email automatically ({(interviewFor.experience_status || "Fresher")})</span></p>
                <ul className="mt-2 grid gap-1 text-xs text-slate-300 list-disc pl-4">
                  <li>Printed copy of resume</li>
                  <li>Government photo ID (Aadhaar / PAN / Passport / Driving Licence)</li>
                  <li>Original + photocopy of education certificates and mark sheets</li>
                  {(interviewFor.experience_status || "").toLowerCase() === "experienced" && <li className="text-nx-goldlight">Experience / relieving letters and last 3 months' salary slips</li>}
                  <li>2 passport-size photographs</li>
                </ul>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button type="button" onClick={() => setInterviewFor(null)} className="text-xs tracking-[0.12em] uppercase text-slate-400 hover:text-white px-4 py-2.5" data-testid="interview-cancel">Cancel</button>
              <button type="submit" disabled={sendingInterview} className="inline-flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-2.5 transition-colors disabled:opacity-60" data-testid="interview-send">
                <Send size={14} /> {sendingInterview ? "Sending…" : "Send Interview Email"}
              </button>
            </div>
          </form>
        </div>
      )}
      {remarkFor && (
        <div data-lenis-prevent className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="remark-modal">
          <form onSubmit={saveRemark} className="bg-nx-navy border border-white/15 w-full max-w-md flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-white/10">
              <div className="min-w-0">
                <h3 className="font-marcellus text-xl text-white inline-flex items-center gap-2"><MessageSquarePlus size={18} className="text-violet-300" /> Add Remark</h3>
                <p className="text-xs text-slate-400 mt-1 truncate">{remarkFor.first_name} {remarkFor.surname} · {remarkFor.position} · {remarkFor.reference_number}</p>
              </div>
              <button type="button" onClick={() => setRemarkFor(null)} className="text-slate-400 hover:text-white" aria-label="Close" data-testid="remark-close"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 flex flex-col gap-4">
              <label className="block"><span className="text-[10px] tracking-[0.16em] uppercase text-slate-500">Remark</span>
                <textarea required rows={3} maxLength={1000} value={remarkForm.remark} onChange={(e) => setRemarkForm((p) => ({ ...p, remark: e.target.value }))} placeholder='e.g. "Candidate requested one year to join"' className="mt-1 w-full bg-white/5 border border-white/15 text-white text-sm px-3 py-2 focus:border-nx-gold/60 outline-none resize-y" data-testid="remark-text" /></label>
              <label className="block"><span className="text-[10px] tracking-[0.16em] uppercase text-slate-500">Follow-up Date</span>
                <input required type="date" value={remarkForm.followup_date} onChange={(e) => setRemarkForm((p) => ({ ...p, followup_date: e.target.value }))} className="mt-1 w-full bg-white/5 border border-white/15 text-white text-sm px-3 py-2 focus:border-nx-gold/60 outline-none" data-testid="remark-date" /></label>
              <p className="text-[11px] text-slate-500">On the follow-up date the candidate receives one "Your Job Application at NexSecure Technologies" reminder. If they don't reply to hr@ within 7 days the application is auto-closed and you are notified on Telegram.</p>
              {(remarkFor.remarks || []).length > 0 && (
                <div className="border border-white/10 divide-y divide-white/5" data-testid="remark-history">
                  {remarkFor.remarks.slice().reverse().map((r) => (
                    <div key={r.id} className="px-3 py-2 text-xs"><p className="text-slate-200">{r.remark}</p><p className="text-slate-500 mt-0.5">Follow-up {fmtInterviewDate(r.followup_date)} · {r.by} · {fmtDate(r.at)}{r.reminder_sent_at ? " · reminder sent" : ""}{r.replied_at ? " · replied" : ""}{r.closed_at ? " · auto-closed" : ""}</p></div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button type="button" onClick={() => setRemarkFor(null)} className="text-xs tracking-[0.12em] uppercase text-slate-400 hover:text-white px-4 py-2.5" data-testid="remark-cancel">Cancel</button>
              <button type="submit" disabled={savingRemark} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-2.5 transition-colors disabled:opacity-60" data-testid="remark-save">{savingRemark ? "Saving…" : "Save Remark"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
